import { Box, InputAdornment, MenuItem } from "@mui/material";
import React from "react";
import DividerWithText from "../../../../components/DividerWithText";
import DivContainerBody from "../../../../components/DivContainerBody";
import { CustomCheckbox } from "../../../../components/CustomCheckbox";
import { timeZone } from "../../../../enums/timeZoneEnum";
import { CustomSelectAndChildren } from "../../../../components/CustomSelectAndChildren";
import { CustomTextField } from "../../../../components/CustomTextField";
import {DEVICE_MODEL_NAME, DISTANCE_MODEL} from "../Model/Model";

/**
 * Provides a group of checkboxes and form fields for configuring device display options,
 * access controls, and hardware features. Options availability is dynamically controlled
 * based on device type using predefined group arrays.
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * @param {Object} data - Current device configuration data
 * @param {Function} handleChange - Universal change handler for all form inputs
 * @param {Function} t - Translation function for internationalization
 * @param {boolean} emptyFields - Flag indicating validation state for required fields
 * @returns {JSX.Element}
 * @constructor
 */

const SectionCreateDeviceOptions = ({ data, handleChange, t,emptyFields, deviceTypeDistance, defaultDistance }) => {
    // Device type groups for conditional disabling
    const disableGroup1 = [
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR];  // Disables primary checkbox group

    const enableGroup2 = [
        DEVICE_MODEL_NAME.TELPO_F6,
        DEVICE_MODEL_NAME.TELPO_F10,
        DEVICE_MODEL_NAME.TELPO_F6_T,
        DEVICE_MODEL_NAME.ARATEK_BM7550,
        DEVICE_MODEL_NAME.ARATEK_BA8300];        // Enables LED display option

    const enableGroup3 = [
        DEVICE_MODEL_NAME.TELPO_F6,
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_F10,
        DEVICE_MODEL_NAME.TELPO_F6_T,
        DEVICE_MODEL_NAME.ARATEK_BM7550,
        DEVICE_MODEL_NAME.ARATEK_BA8300];        // Enables LED display option

    const enablePrinter = [
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_K10];                     // Enables printer option

    const disableTimeZone = [
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ANTENNA_RFID];     // Disable time zone

    const disableDistance = [
        DEVICE_MODEL_NAME.VERIDAS_1FA,
        DEVICE_MODEL_NAME.VERIDAS_2FA,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.PDA_CARIBE_PL50L,
        DEVICE_MODEL_NAME.MANTRA_MFSTAB_II,
        DEVICE_MODEL_NAME.TELPO_TPS_450,
        DEVICE_MODEL_NAME.TELPO_TPS_980,
        DEVICE_MODEL_NAME.TELPO_K5,
    ];     // Disable distance

    return (
        <DivContainerBody>
            <div className={"row"}>
                <div className={"col-12"}>
                    <DividerWithText title={t("options")} spacing={"mb-4"} />
                </div>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_logos"
                        checked={data.showLogo}
                        value={data.showLogo}
                        onChange={(e) => handleChange(e)}
                        labelId="lbl_cud_showLogos"
                        label="show_logos"
                        trans="show_logos"
                        name="showLogo"
                        disabled={true}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_user_list"
                        checked={data.showUserList}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_showLogos"}
                        label={"show_user_list"}
                        trans={"show_user_list"}
                        name={"showUserList"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_synchorize_selfies"
                        checked={data.synchorizeSelfies}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_synchorizeSelfies"}
                        label={"synchronize_photographs"}
                        trans={"synchronize_photographs"}
                        name={"synchorizeSelfies"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        id="cb_cud_play_sounds"
                        marginbutton={true}
                        checked={data.playSounds}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_playSounds"}
                        label={"playSound"}
                        trans={"playSound"}
                        name={"playSounds"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_weapon"
                        checked={data.showWeapon}
                        onChange={(e) => handleChange(e)}
                        labelId={"cb_cud_showWeapon"}
                        label={"show_weapon"}
                        trans={"show_weapon"}
                        name={"showWeapon"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_vehicles"
                        checked={data.showVehicle}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_showVehicle"}
                        label={"show_vehicle"}
                        trans={"show_vehicle"}
                        name={"showVehicle"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_led"
                        checked={data.showLed}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_showLed"}
                        label={"show_led"}
                        trans={"turn_on_led"}
                        name={"showLed"}
                        disabled={!enableGroup2.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_access_dialog"
                        checked={data.showAccessDialog}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_showAccessDialog"}
                        label={"show_access_dialog"}
                        trans={"show_access_dialog"}
                        name={"showAccessDialog"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_show_last_access"
                        checked={data.showLastAccess}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_showLastAccess"}
                        label={"show_last_access"}
                        trans={"show_last_access"}
                        name={"showLastAccess"}
                        disabled={disableGroup1.includes(data.deviceType)}
                    />
                </Box>
                <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <CustomCheckbox
                        marginbutton={true}
                        className={"grid-checkall"}
                        id="cb_cud_printer"
                        checked={data.printer}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_printer"}
                        label={"enable_printer"}
                        trans={"enable_printer"}
                        name={"printer"}
                        disabled={!enablePrinter.includes(data.deviceType)}
                    />
                </Box>
                <div className={"col-12 row mt-4 p-0"}>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomSelectAndChildren
                            id="slt_cud_time_zone"
                            name="timeZoneValue"
                            required={true}
                            trans="time_zone"
                            label="lbl_cud_zone"
                            showNoSelectionOption={false}
                            value={data.timeZoneValue}
                            onChange={(evt, name) => {
                                handleChange(evt, "timeZoneValue");
                            }}
                            emptyFields={
                                emptyFields &&
                                !disableTimeZone.includes(data.deviceType)
                            }
                            disabled={disableTimeZone.includes(data.deviceType)}
                        >
                            {Object.entries(timeZone).map(([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    {t(value)}
                                </MenuItem>
                            ))}
                        </CustomSelectAndChildren>
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomTextField
                            id="txt_cud_consecutive_openings_allowed"
                            name="consecutiveOpeningsAllowed"
                            trans="number_attemps"
                            value={data.consecutiveOpeningsAllowed}
                            onChange={(e) => {
                                const value = e.target.value;
                                const regex = /^[0-9]*$/;
                                if (regex.test(value) && (value === "" || parseInt(value, 10) >= 0)) {
                                    handleChange(e);
                                }
                            }}
                            lenght={4}
                            required={true}
                            emptyFields={
                                emptyFields &&
                                data.deviceType !== "" &&
                                enableGroup3.includes(data.deviceType)
                            }
                            disabled={!enableGroup3.includes(data.deviceType)}
                            helperText={"zero_means_unlimited"}
                            showHelperTextAlways={data?.consecutiveOpeningsAllowed !== ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end" />
                                ),
                            }}
                        />
                    </Box>
                </div>
                <div className={"col-12 row p-0"}>
                      <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomCheckbox
                        marginbutton={true}
                        id="cb_cud_enable_distance"
                        checked={data.enableDistance}
                        onChange={(e) => handleChange(e)}
                        labelId={"lbl_cud_enable_distance"}
                        label={"enable_distance"}
                        trans={"enable_distance"}
                        name={"enableDistance"}
                        disabled={disableDistance.includes(data.deviceType)}
                    />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomSelectAndChildren
                            id="slt_cud_distance"
                            name="distance"
                            required={data.enableDistance === true}
                            trans="distance"
                            label="lbl_cud_distance"
                            showNoSelectionOption={false}
                            value={data.enableDistance ? (data.distance ?? '') : defaultDistance}
                            onChange={(evt, name) => {
                                handleChange(evt, "distance");
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end" />
                                ),
                            }}
                            emptyFields={
                                emptyFields &&
                                !disableDistance.includes(data.deviceType) &&
                                data.enableDistance &&
                                (data.distance == null || data.distance === '' || Number(data.distance) === 0)
                            }
                            disabled={disableDistance.includes(data.deviceType) || !data.enableDistance}
                        >
                            {deviceTypeDistance.map((value) => (
                                <MenuItem key={value} value={value}>
                                     {`${value} CM`}
                                </MenuItem>
                            ))}
                        </CustomSelectAndChildren>
                    </Box>

                </div>
            </div>
        </DivContainerBody>
    );
};

export default SectionCreateDeviceOptions;
